import { EndedBy } from '../types';

/**
 * 结局文案与判定规则表（结局批 2026-09-29，设计=docs/实现说明.md §3.4g + 迁存章 A5）
 *
 * 结构：基调层（咖位六档，模板挖数字坑）× 形状层（互斥修饰词，按序命中至多一个）；
 * 失败结局（stage_fail）独立四段 + 动态建议三档（⛔ 数字差距句式，2026-09-29 拍板）；
 * 隐藏层（H1~H4）整层后置 P2，⛔ 本批不实装（候选清单在设计文档 §2.3）。
 * 文案为首版（用户拍板「先按 AI 写的来，后面可能再改」），改文案只动本表。
 * 模板占位符：%age% %works% %awards% %fans% %rank%（%awards% 为短语坑：获奖>0 填「N 座奖杯」）。
 */

/** 基调档位：按咖位降序查找，第一个 rank ≥ minRank 的档位即命中 */
export interface EndingTierDef {
    id: string;
    minRank: number;
    title: string;
    template: string;
}

export const ENDING_TIERS: EndingTierDef[] = [
    {
        id: 'T1', minRank: 10, title: '传奇谢幕',
        template: '十八年，%works% 部作品，%awards%，%fans%粉丝。%age% 岁的你以国际巨星之位谢幕——红毯的尽头再无对手，行业史里从此有你的名字。从无人认识的练习生到一代人的银幕记忆，这条路的每一步，都算数。',
    },
    {
        id: 'T2', minRank: 8, title: '名满天下',
        template: '%age% 岁谢幕时，你的名字已经家喻户晓。%works% 部作品、%awards%，%fans%粉丝一路见证你从二线走到行业之巅。长辈们认得你的海报，业内尊称一声老师——「国民度」这三个字，你当得起。',
    },
    {
        id: 'T3', minRank: 6, title: '巅峰之位',
        template: '%works% 部作品垒起了这一程的高度。%age% 岁的你以%rank%之名离场：热搜有过你的位置，代言排过你的档期，%fans%粉丝还在等你的下一部戏。顶流的世界你来过，也留下过姓名。',
    },
    {
        id: 'T4', minRank: 4, title: '稳站一方',
        template: '不是所有故事都有惊天动地的结局。%age% 岁的你以%rank%的身份谢幕，%works% 部作品、%fans%粉丝——选角导演记得你的名字，观众记得你的脸。在这个圈子里站稳脚跟，已经是大多数人做不到的事。',
    },
    {
        id: 'T5', minRank: 2, title: '小有所成',
        template: '%works% 部作品，%fans%粉丝，偶尔被人在街上叫出角色名。%age% 岁的你带着「%rank%」的身份离开了这个圈子。不算耀眼，但你真的来过、演过、被看见过。',
    },
    {
        id: 'T6', minRank: 1, title: '散场',
        template: '聚光灯没有为你停留。%age% 岁、%works% 部作品，你的名字最终没能在圈内留下回响。但那些试镜的等待、深夜的台词、跑组的日夜都真实发生过。散场之后，人生还有别的剧本。',
    },
];

/** 按咖位取基调档位（数组序=优先级，T1 最先） */
export function tierOfRank(rank: number): EndingTierDef {
    return ENDING_TIERS.find(t => rank >= t.minRank) ?? ENDING_TIERS[ENDING_TIERS.length - 1];
}

/** 形状规则上下文（由终局 PlayerState 聚合，见 EndingSystem.buildSummary） */
export interface ShapeContext {
    /** 奖项获奖数 */
    won: number;
    /** 生涯作品数（filmography） */
    works: number;
    /** 电影履历占比 0~1 */
    movieShare: number;
    /** 履历评分均分 */
    avgScore: number;
    /** 粉丝/名气比（fans / max(1, fame)） */
    fansPerFame: number;
    /** 累计完成通告数 */
    totalShots: number;
    /** 履历是否全为 short */
    allShort: boolean;
}

/** 形状规则（互斥，按序命中至多一个；阈值=首版对账值，依据见设计文档 §2.2 与对账记录） */
export interface ShapeRuleDef {
    id: string;
    prefix: string;
    test: (c: ShapeContext) => boolean;
}

export const SHAPE_RULES: ShapeRuleDef[] = [
    // 阈值对账（2026-09-29，4 档×4 条真接口生涯；脚本已清理，git 可查）：首版 won≥2 命中 11/16
    // （normal 档 won=2 全中）⇒ 收紧 ≥3：normal(won=2) 落到 S5 劳模（120 部只拿 2 奖=劳模本义），good/maxed(won≥10) 稳中。
    { id: 'S1', prefix: '奖杯加冕的', test: c => c.won >= 3 },
    { id: 'S2', prefix: '电影咖的', test: c => c.works >= 3 && c.movieShare >= 0.6 && c.avgScore >= 7.0 },
    // 首版 2800 全灭（16 条实测最大 2715；fans/fame 理论上限≈2000×1.35×1.06≈2862）⇒ 放宽 2600=号召力上 ~20% 分位。
    { id: 'S3', prefix: '顶流体质的', test: c => c.fansPerFame >= 2600 },
    { id: 'S4', prefix: '作品为冠的', test: c => c.works >= 5 && c.avgScore >= 7.5 },
    { id: 'S5', prefix: '劳模的', test: c => c.totalShots >= 40 },
    { id: 'S6', prefix: '短剧女王的', test: c => c.works >= 5 && c.allShort },
];

/** 失败结局标题（统一；分段文案按阶段区分） */
export const FAIL_TITLE = '星途中断';

/** 阶段审查失败总结（按阶段 1-4，措辞递进；⛔ 惩罚腔；%works% 可用） */
export const STAGE_FAIL_COPY: string[] = [
    // 阶段一（22-25）：圈内眼熟 Lv.3
    // 【正式文案 2026-10-21 茄子手写】源文档 docs/主线剧情/审查线剧情文案.md §1.3b。
    // 收尾的「下一次，……」由 failAdviceOf 另行给出，此处不重复。
    '二十五岁这年，你拖着行李箱回了家。三年之约没有兑现，箱底那几本磨了角的剧本，到底没能换来一个被记住的名字。可片场的灯你站进去过，镜头也真真切切对准过你——那不是白走的路。',
    // 阶段二（25-30）：二线艺人 Lv.6（重排顺移）
    '二十五到三十岁，是圈子里最拥挤的五年。这一程停在了审查线上，有些可惜——但你的履历不至于无人在意，调整行囊，随时可以重新出发。',
    // 阶段三（30-35）：顶流艺人 Lv.8（重排顺移）
    '三十岁之后，每一步都要用作品说话。这一次没能跨过审查线，可 %works% 部作品还在，路也还在。',
    // 阶段四（35-40）：无审查，自由冲刺
    '行至最后一段，终究没能以想要的高度谢幕。可这十八年里的等待、爆发与坚持都是真的——故事结束在这里，不代表它不值得。',
];

/** 失败动态建议（按 Δ = requiredRank − rank 三档定性措辞，2026-09-29 拍板） */
export function failAdviceOf(delta: number): string {
    if (delta >= 3) return '这条赛道需要从头再来——先站稳脚跟，再谈登顶。';
    if (delta === 2) return '下一次，把作品的数量和名气再攒一攒。';
    return '下一次，多接一部戏就能过关。';
}

/** 尾句池（natural/voluntary 共用基调模板，尾句区分结束方式） */
export const TAIL_NATURAL = '十八年之期已到，这场长跑，你跑完了全程。';
export const TAIL_VOLUNTARY = '在这一年，你亲手为这个故事画上了句号。';

/** 结束方式的日志用名 */
export function endedByLabel(endedBy: EndedBy): string {
    return endedBy === 'natural' ? '正常退役' : endedBy === 'voluntary' ? '主动退圈' : '阶段审查未通过';
}
